import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Perimeters from './Perimeters'
import { useFormContext, useWatch } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import { Form, Tab } from 'semantic-ui-react'
import ColorPicker from '~/components/ColorPicker'
import { ThemeContext } from 'styled-components'
import { El } from '~/components/Layout/style'
import { StyledTabPane } from './style'
import { StyledMenuItem } from '../../../General/style'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, XAxis, YAxis } from 'recharts'

interface Props {
  startingColVal: {
    yourValueColor: string
    bayernValueColor: string
  }
}

const General: React.FC<Props> = ({ startingColVal }) => {
  const { t } = useTranslation()
  const { control, setValue, getValues, reset, formState } = useFormContext()
  const { firstColor, secondaryColor } = useContext(ThemeContext)
  const data = [
    { name: t(''), uv: 4000 },
    { name: t(''), pv: 1398 }
  ];
  const watchPrimaryColor = useWatch({ control, name: 'yourValueColor' })
  const watchSecondaryColor = useWatch({ control, name: 'bayernValueColor' })

  const [activeTabIndex, setActiveTabIndex] = useState<string | number>(0)

  function renderCo2Settings(): React.ReactNode {
    return <div>
      <FormFields.Checkbox
        control={control}
        name='showLflDefaultValue'
        defaultValue={0}
        checkboxValue={1}
        label={t('Show LfL standard values ​​in the dashboard/reporting')}
      />
      <FormFields.Checkbox
        control={control}
        name='showResultsInLiveStatus'
        defaultValue={0}
        checkboxValue={1}
        label={t('Show results in live status')}
      />
      <FormFields.Checkbox
        control={control}
        name='useMilkPrice'
        defaultValue={0}
        checkboxValue={1}
        label={t('Display milk price')}
      />
      <FormFields.Checkbox
        control={control}
        name='useAdditivesTabOnCollectingData'
        defaultValue={0}
        checkboxValue={1}
        label={'”Einsatz Zusatzstoffe” bei Datenerhebungen abfragen?'}
      />
      <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column' }}>
        <Form.Group style={{ alignItems: 'center' }}>
          <FormFields.External
            fluid
            name={'yourValueColor'}
            label={t('Primary color')}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('Please enter color')
              }
            }}
          >
            <ColorPicker value={watchPrimaryColor || ''}>
              <FormFields.Input
                control={control}
                hideError
                onChange={(e, data) => setValue('yourValueColor', data.value)}
                placeholder={t('Primary color')}
                name={'yourValueColor'}
              />
            </ColorPicker>
          </FormFields.External>
          <FormFields.External
            fluid
            name={'bayernValueColor'}
            label={t('Secondary color')}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('Please enter color')
              }
            }}
          >
            <ColorPicker value={watchSecondaryColor || ''}>
              <FormFields.Input
                control={control}
                hideError
                onChange={(e, data) => setValue('bayernValueColor', data.value)}
                placeholder={t('Secondary color')}
                name={'bayernValueColor'}
              />
            </ColorPicker>
          </FormFields.External>
          <El.PageHeaderActionButton
            style={{ marginTop: 22 }}
            type='button'
            onClick={() => {
              reset({
                ...getValues(),
                yourValueColor: startingColVal.yourValueColor,
                bayernValueColor: startingColVal.bayernValueColor
              })
            }}>
            {t('Reset')}
          </El.PageHeaderActionButton>
          <El.PageHeaderActionButton
            style={{ marginTop: 22 }}
            type='button'
            onClick={() => {
              reset({
                ...getValues(),
                yourValueColor: firstColor,
                bayernValueColor: secondaryColor || firstColor
              })
            }}>
            {t('Theme colors')}
          </El.PageHeaderActionButton>
        </Form.Group>
      </div>
      {renderChartColorExample()}
      {renderLanguageTabs()}
    </div>
  }

  function getMenuItem(label, index) {
    const languagesErrors = formState.errors?.texts ? formState?.errors?.texts[0]?.languages : {}
    const paneHasError = index in (languagesErrors || {});
    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageTabs() {
    const languages = getValues('texts[0].languages')
    return <Tab
      activeIndex={activeTabIndex}
      onTabChange={(_, data) => setActiveTabIndex(data.activeIndex)}
      renderActiveOnly={false}
      menu={{ secondary: true, pointing: true }}
      panes={languages?.map((language, index) => {
        return {
          menuItem: getMenuItem(language.label, index),
          key: language.id,
          pane: <StyledTabPane
            active={index === activeTabIndex}
            key={`${language.id}-${index}`}
          >
            <FormFields.Input
              control={control}
              name={`texts[0].languages[${index}].translation`}
              label={t('Average THG Emissions Chart Text')}
              placeholder={t('Average THG Emissions Chart Text')}
              rules={{
                required: {
                  value: true,
                  message: t('Please enter Average GHG Emissions Chart Text')
                }
              }}
            />
          </StyledTabPane>
        }
      })}
    />
  }

  function renderChartColorExample() {
    return <ResponsiveContainer width='100%' height={250}>
      <BarChart
        layout='vertical' // Set the layout to vertical to align bars along the y-axis
        data={data}
        margin={{ top: 20, right: 30, left: -60, bottom: 5 }}
        maxBarSize={30}
      >
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis type='number' />
        <YAxis type='category' dataKey='name' />
        <Legend />
        <Bar dataKey='uv' stackId='a' fill={watchPrimaryColor} />
        <Bar dataKey='pv' stackId='a' fill={watchSecondaryColor} />
      </BarChart>
    </ResponsiveContainer>
  }

  return <div style={{ padding: 20 }}>
    {renderCo2Settings()}
    <Perimeters
      control={control}
    />
    <FormFields.Input
      label={t('Comparison Results Count')}
      control={control}
      name='comparisonResultsNumber'
      styleControlled={{ width: 250 }}
      typeInput={'controled'}
      rules={{
        required: true
      }}
      noErrorMessage
      placeholder={t('Please enter result count')}
    />
  </div>
}
export default General